import React, { useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import { useData } from '../App';
import { AppView } from '../types';
import { MapPin, GraduationCap, Home, ArrowRight } from 'lucide-react';

interface MapExplorerProps {
  setView: (view: AppView) => void;
}

// Helper to create custom HTML icons
const createIcon = (type: 'neighborhood' | 'school') => {
  const colorClass = type === 'neighborhood' ? 'bg-indigo-600' : 'bg-emerald-600';
  const iconHtml = type === 'neighborhood' 
    ? `<div class="${colorClass} w-8 h-8 rounded-full flex items-center justify-center text-white border-2 border-white shadow-lg"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg></div>`
    : `<div class="${colorClass} w-8 h-8 rounded-full flex items-center justify-center text-white border-2 border-white shadow-lg"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></svg></div>`;

  return L.divIcon({
    className: 'custom-icon',
    html: iconHtml,
    iconSize: [32, 32],
    iconAnchor: [16, 32],
    popupAnchor: [0, -32],
  });
};

const neighborhoodIcon = createIcon('neighborhood');
const schoolIcon = createIcon('school');

const MapExplorer: React.FC<MapExplorerProps> = ({ setView }) => {
  const { neighborhoods, schools } = useData();
  const [filter, setFilter] = useState<'all' | 'neighborhoods' | 'schools'>('all');
  
  // Center of Pune
  const centerPosition: [number, number] = [18.5204, 73.8567];

  return (
    <div className="relative w-full h-[calc(100vh-5rem)] md:h-screen z-0">
      {/* Pune scope badge */}
      <div className="absolute top-4 right-4 z-[400]">
        <span className="text-xs font-bold px-3 py-1.5 bg-amber-100 text-amber-700 rounded-full shadow-sm border border-amber-200">
          📍 Pune
        </span>
      </div>
      {/* Map Controls Overlay */}
      <div className="absolute top-4 left-4 z-[400] bg-white p-2 rounded-lg shadow-md flex flex-col gap-2 border border-slate-200">
        <button 
          onClick={() => setFilter('all')}
          className={`px-3 py-1.5 text-xs font-medium rounded-md transition ${filter === 'all' ? 'bg-slate-800 text-white' : 'bg-slate-50 text-slate-600 hover:bg-slate-100'}`}
        >
          Show All
        </button>
        <button 
          onClick={() => setFilter('neighborhoods')}
          className={`px-3 py-1.5 text-xs font-medium rounded-md flex items-center gap-2 transition ${filter === 'neighborhoods' ? 'bg-indigo-600 text-white' : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100'}`}
        >
          <Home size={12} /> Neighborhoods
        </button>
        <button 
          onClick={() => setFilter('schools')}
          className={`px-3 py-1.5 text-xs font-medium rounded-md flex items-center gap-2 transition ${filter === 'schools' ? 'bg-emerald-600 text-white' : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'}`}
        >
          <GraduationCap size={12} /> Schools
        </button>
      </div>

      <MapContainer 
        center={centerPosition} 
        zoom={12} 
        scrollWheelZoom={true} 
        className="w-full h-full"
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />

        {(filter === 'all' || filter === 'neighborhoods') && neighborhoods
          .filter(area => area.lat != null && area.lng != null)
          .map(area => (
          <Marker
            key={`nh-${area.id}`}
            position={[area.lat, area.lng]}
            icon={neighborhoodIcon}
          >
            <Popup>
              <div className="p-1 min-w-[200px]">
                <h3 className="font-bold text-lg text-slate-800 mb-1">{area.name}</h3>
                <p className="text-xs text-slate-500 mb-2 capitalize">{area.microMarket.replace('_', ' ')}</p>
                <div className="flex justify-between text-xs mb-3 font-medium">
                  <span className="text-indigo-600">
                    Rent: {(area.propertyPriceRange.rent3bhk.min/1000).toFixed(0)}k-{(area.propertyPriceRange.rent3bhk.max/1000).toFixed(0)}k
                  </span>
                  <span className="text-emerald-600">NRI Score: {area.nriCommunityScore}/10</span>
                </div>
                <button 
                  onClick={() => setView(AppView.NEIGHBORHOODS)}
                  className="w-full bg-indigo-50 text-indigo-700 py-1.5 rounded text-xs font-semibold hover:bg-indigo-100 transition flex items-center justify-center"
                >
                  View Profile <ArrowRight size={12} className="ml-1" />
                </button>
              </div>
            </Popup>
          </Marker>
        ))}

        {(filter === 'all' || filter === 'schools') && schools
          .filter(school => school.lat != null && school.lng != null)
          .map(school => (
          <Marker
            key={`sc-${school.id}`}
            position={[school.lat, school.lng]}
            icon={schoolIcon}
          >
            <Popup>
               <div className="p-1 min-w-[200px]">
                <h3 className="font-bold text-lg text-slate-800 mb-1">{school.name}</h3>
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  {school.curriculum.slice(0, 2).map(c => (
                    <span key={c} className="text-[10px] px-1.5 py-0.5 bg-slate-100 rounded border border-slate-200 font-medium">
                      {c}
                    </span>
                  ))}
                  <span className="text-[10px] text-slate-500">{school.location}</span>
                </div>
                <div className="flex justify-between text-xs mb-3 font-medium">
                  <span className="text-slate-600">
                    Fee: ₹{(school.feeRangeAnnual.min/100000).toFixed(1)}L - {(school.feeRangeAnnual.max/100000).toFixed(1)}L
                  </span>
                </div>
                <button 
                  onClick={() => setView(AppView.SCHOOLS)}
                  className="w-full bg-emerald-50 text-emerald-700 py-1.5 rounded text-xs font-semibold hover:bg-emerald-100 transition flex items-center justify-center"
                >
                  View Details <ArrowRight size={12} className="ml-1" />
                </button>
              </div>
            </Popup>
          </Marker>
        ))}

      </MapContainer>
    </div>
  );
};

export default MapExplorer;